"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAnalytics = void 0;
var web_1 = require("@react-hookz/web");
var next_plausible_1 = require("next-plausible");
var react_1 = require("react");
var AnalyticsProvider_1 = require("../AnalyticsProvider");
var lib_1 = require("../lib");
var types_1 = require("../types");
var useQueue_1 = require("./useQueue");
var DEFERRED_IDENTITY_STORAGE_KEY = 'prezly_ajs_deferred_identity';
var NULL_USER = {
    id: function () {
        return null;
    },
    anonymousId: function () {
        return null;
    },
};
function useAnalytics() {
    var _a = (0, AnalyticsProvider_1.useAnalyticsContext)(), analytics = _a.analytics, consent = _a.consent, gallery = _a.gallery, isEnabled = _a.isEnabled, newsroom = _a.newsroom, story = _a.story, trackingPolicy = _a.trackingPolicy;
    var plausible = (0, next_plausible_1.usePlausible)();
    var _b = newsroom || {
        uuid: undefined,
        is_plausible_enabled: false,
    }, newsroomUuid = _b.uuid, isPlausibleEnabled = _b.is_plausible_enabled;
    var storyUuid = story === null || story === void 0 ? void 0 : story.uuid;
    var galleryUuid = gallery === null || gallery === void 0 ? void 0 : gallery.uuid;
    // We use ref to `analytics` object, cause our tracking calls are added to the callback queue, and those need to have access to the most recent instance if `analytics`,
    // which would not be possible when passing the `analytics` object directly
    var analyticsRef = (0, web_1.useSyncedRef)(analytics);
    var plausibleRef = (0, web_1.useSyncedRef)(plausible);
    var _c = __read((0, web_1.useLocalStorageValue)(DEFERRED_IDENTITY_STORAGE_KEY), 3), deferredIdentity = _c[0], setDeferredIdentity = _c[1], removeDeferredIdentity = _c[2];
    var _d = (0, useQueue_1.useQueue)(), addToQueue = _d.add, removeFromQueue = _d.remove, firstInQueue = _d.first;
    // The prezly traits should be placed in the root of the event when sent to the API.
    // This is handled by the `normalizePrezlyMeta` plugin.
    var injectPrezlyMeta = (0, react_1.useCallback)(function (traits) {
        if (!newsroomUuid) {
            return traits;
        }
        return __assign(__assign({}, traits), { prezly: __assign(__assign(__assign({ newsroom: newsroomUuid }, (storyUuid && {
                story: storyUuid,
            })), (galleryUuid && {
                gallery: galleryUuid,
            })), (trackingPolicy !== types_1.TrackingPolicy.DEFAULT && {
                tracking_policy: trackingPolicy,
            })) });
    }, [galleryUuid, newsroomUuid, storyUuid, trackingPolicy]);
    var identify = (0, react_1.useCallback)(function (userId, traits, callback) {
        if (traits === void 0) { traits = {}; }
        var extendedTraits = injectPrezlyMeta(traits);
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log("analytics.identify(".concat((0, lib_1.stringify)(userId, extendedTraits), ")"));
        }
        if (trackingPolicy === types_1.TrackingPolicy.CONSENT_TO_IDENTIFY && !consent) {
            setDeferredIdentity({ userId: userId, traits: extendedTraits });
            if (callback) {
                callback();
            }
            return;
        }
        addToQueue(function () {
            if (analyticsRef.current && analyticsRef.current.identify) {
                analyticsRef.current.identify(userId, extendedTraits, {}, callback);
            }
        });
    }, [addToQueue, analyticsRef, consent, setDeferredIdentity, trackingPolicy, injectPrezlyMeta]);
    var alias = (0, react_1.useCallback)(function (userId, previousId) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log("analytics.alias(".concat((0, lib_1.stringify)(userId, previousId), ")"));
        }
        addToQueue(function () {
            if (analyticsRef.current && analyticsRef.current.alias) {
                analyticsRef.current.alias(userId, previousId);
            }
        });
    }, [addToQueue, analyticsRef]);
    var page = (0, react_1.useCallback)(function (category, name, properties, callback) {
        if (properties === void 0) { properties = {}; }
        var extendedProperties = injectPrezlyMeta(properties);
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log("analytics.page(".concat((0, lib_1.stringify)(category, name, extendedProperties), ")"));
        }
        addToQueue(function () {
            if (analyticsRef.current && analyticsRef.current.page) {
                analyticsRef.current.page(category, name, extendedProperties, {}, callback);
            }
        });
    }, [addToQueue, analyticsRef, injectPrezlyMeta]);
    var track = (0, react_1.useCallback)(function (event, properties, callback) {
        if (properties === void 0) { properties = {}; }
        var extendedProperties = injectPrezlyMeta(properties);
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log("analytics.track(".concat((0, lib_1.stringify)(event, extendedProperties), ")"));
        }
        addToQueue(function () {
            if (analyticsRef.current && analyticsRef.current.track) {
                analyticsRef.current.track(event, extendedProperties, {}, callback);
            }
            if (isPlausibleEnabled) {
                plausibleRef.current(event, { props: extendedProperties });
            }
        });
    }, [addToQueue, analyticsRef, injectPrezlyMeta, isPlausibleEnabled, plausibleRef]);
    var user = (0, react_1.useCallback)(function () {
        if (analytics && analytics.user) {
            return analytics.user();
        }
        // Return fake user API to keep code working even without analytics.js loaded
        return NULL_USER;
    }, [analytics]);
    (0, react_1.useEffect)(function () {
        // We are using simple queue to trigger tracking calls
        // that might have been created before analytics.js was loaded.
        if (analytics && firstInQueue) {
            firstInQueue();
            removeFromQueue();
        }
    }, [firstInQueue, analytics, removeFromQueue]);
    (0, react_1.useEffect)(function () {
        if (consent) {
            if (deferredIdentity) {
                var userId = deferredIdentity.userId, traits = deferredIdentity.traits;
                identify(userId, traits);
                removeDeferredIdentity();
            }
        }
        else {
            var id = user().id();
            if (id && (deferredIdentity === null || deferredIdentity === void 0 ? void 0 : deferredIdentity.userId) !== id) {
                setDeferredIdentity({ userId: id });
            }
            user().id(null); // erase user ID
        }
    }, [consent, deferredIdentity, identify, user, removeDeferredIdentity, setDeferredIdentity]);
    if (!isEnabled || trackingPolicy === types_1.TrackingPolicy.DISABLED) {
        return {
            alias: function () { },
            identify: function () { },
            page: function () { },
            track: function () { },
            user: user,
        };
    }
    return {
        alias: alias,
        identify: identify,
        newsroom: newsroom,
        page: page,
        track: track,
        user: user,
    };
}
exports.useAnalytics = useAnalytics;
//# sourceMappingURL=useAnalytics.js.map